import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client/react'
import type { DocumentNode } from 'graphql'

import FetchMore from 'js/components/FetchMore'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'

import NoResultsMessage from './NoResultsMessage/NoResultsMessage'

type Entity = {
    guid: string
    [key: string]: unknown
}

type FetchMoreWrapperProps = {
    offset: number
    limit: number
    childComponent: React.ComponentType<{ entity: Entity }>
    query: DocumentNode
    containerGuid?: string
    groupGuid?: string
    subtype?: string
    tags?: string[]
}

const FetchMoreWrapper = ({
    offset,
    limit,
    childComponent: Component,
    query,
    containerGuid,
    groupGuid,
    subtype,
    tags,
}: FetchMoreWrapperProps) => {
    const { t } = useTranslation()
    const PAGE_SIZE = limit

    const { loading, data, fetchMore } = useQuery<any>(query, {
        variables: {
            offset,
            limit: PAGE_SIZE,
            subtype,
            containerGuid,
            tags,
            groupGuid,
        },
        fetchPolicy: 'cache-and-network',
    })

    if (loading) {
        return <LoadingSpinner />
    }

    if (data?.entities?.total === 0) {
        return (
            <NoResultsMessage
                title={t('widget-feed.no-results')}
                subtitle={t('widget-feed.no-results-helper')}
            />
        )
    }

    if (!data?.entities) {
        return null
    }

    return (
        <FetchMore
            edges={data.entities.edges}
            getMoreResults={(data) => data.entities.edges}
            fetchMore={fetchMore}
            fetchCount={limit}
            maxLimit={data.entities.total}
            resultsMessage={t('global.result', {
                count: data.entities.total,
            })}
            fetchMoreButtonHeight={48}
        >
            {data.entities.edges.map((entity) => (
                <Component key={entity.guid} entity={entity} />
            ))}
        </FetchMore>
    )
}

export default FetchMoreWrapper
